import classNames from "classnames";
import React, {useEffect, useMemo, useState} from "react";
import {ColumnDescription} from "react-bootstrap-table-next";
import {useTranslation} from "react-i18next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import ResidentDetail from "../../../stores/domainStore/pages/models/residentDetail";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import "./residentAdminDataList.sass";
import NoResultsIcon from "../../atoms/noResultsIcon/noResultsIcon";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";
import {useStores} from "../../../stores/useStores";
import {observer} from "mobx-react-lite";
import {ResidentRefiner} from "../../../utils/residentRefiner";
import useWindowDimensions from "../../../utils/ui/useWindowDimensions";
import {
  excludeHeightForResidentAdmin,
  heightOfDataRow
} from "../../../shared/constants";
import AppAccessStatusTab from "../../molecules/appAccessStatusTab/appAccessStatusTab";
import AreaTag from "../../atoms/areaTag/areaTag";
import {IResidentAdminDomainStore} from "../../../stores/domainStore/pages/residentAdminDomainStore";
import {IResidentAdminUiStore} from "../../../stores/uiStore/pages/residentAdminUiStore";

export interface IResidentAdminDataListProps {
  /**
   * An event that gets fired when we wish to show the resident details modal
   */
  showResidentModal: (resident: ResidentDetail) => void;
  // Overrides let the serviced-apartment page reuse this list; default to the independent stores/namespace.
  residentAdminDomainStore?: IResidentAdminDomainStore;
  residentAdminUiStore?: IResidentAdminUiStore;
  namespace?: string;
}

const ResidentAdminDataList: React.FC<IResidentAdminDataListProps> = ({
  showResidentModal,
  residentAdminDomainStore: injectedDomainStore,
  residentAdminUiStore: injectedUiStore,
  namespace = "residentadmin"
}) => {
  const {
    domainStore: {independentResidentAdminDomainStore: defaultDomainStore},
    uiStore: {independentResidentAdminUiStore: defaultUiStore}
  } = useStores();

  const residentAdminDomainStore = injectedDomainStore ?? defaultDomainStore;
  const residentAdminUiStore = injectedUiStore ?? defaultUiStore;

  const {t} = useTranslation(["residentadmin", "saresidentadmin"]);

  const onPageChange = (
    pageNumber: number,
    sizePerPage: number,
    sortField: string,
    sortOrder: SortOrder
  ) => {
    // Reuse the same functionality as sortHandler but cater to the different parameter order
    sortHandler(sortField, sortOrder, pageNumber, sizePerPage);
  };

  const onPageNumber = (pageNumber: number, pageSize: number) => {
    setPageNumber(pageNumber);
  };

  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;
    // Columns opt out with sort: false when the API cannot order by that field.
    const isColumnSortable = canSort && column.sort !== false;

    return (
      <div
        className={classNames("residentadmin-table-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}

        {isColumnSortable && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const statusFormatter = (_: any, resident: ResidentDetail) => {
    return <AppAccessStatusTab appAccessStatus={resident.appAccessStatus} />;
  };

  const areaFormatter = (_: any, resident: ResidentDetail) => {
    return <AreaTag personTypeId={resident.personTypeId} />;
  };
  const lastNameFormatter = (_: any, resident: ResidentDetail) => {
    return (
      <span
        className="resident-modal-control"
        onClick={() => showResidentModal(resident)}
      >
        {resident.lastName}
      </span>
    );
  };

  const emailFormatter = (_: any, resident: ResidentDetail) => {
    return resident.appAccountEmail || "-";
  };

  const noResidentAdminIndication = (
    <div className="residentadmin-data-list-empty">
      <NoResultsIcon
        titleText={
          !residentAdminUiStore.currentResidentAdminListSearchTerm ||
          residentAdminUiStore.currentResidentAdminListSearchTerm === ""
            ? t("residentadmin:messages.noResidentAdmin")
            : t("residentadmin:messages.noSearchResults")
        }
        bodyText={residentAdminUiStore.currentResidentAdminListSearchTerm}
      />
    </div>
  );

  const {height} = useWindowDimensions();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [sortField, setSortField] = useState("lastName");
  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);

  const sizePerPage: number = useMemo(() => {
    return Math.round(
      (height - excludeHeightForResidentAdmin) / heightOfDataRow
    );
  }, [height]);

  // The residents to be displayed on the data table
  // depending on the current page number, sort field, sort order, and search term
  const displayResidents: ResidentDetail[] = useMemo(
    () =>
      ResidentRefiner.refine(
        residentAdminDomainStore.residents,
        pageNumber,
        sizePerPage,
        sortField,
        sortOrder,
        residentAdminUiStore.currentResidentAdminListSearchTerm,
        residentAdminUiStore.statusFilter
      ),
    [
      residentAdminUiStore.currentResidentAdminListSearchTerm,
      residentAdminDomainStore.residents,
      pageNumber,
      sizePerPage,
      sortField,
      sortOrder,
      residentAdminUiStore.statusFilter
    ]
  );

  // The total number of residents - unpaginated
  const displayResidentsLength = useMemo(
    () =>
      ResidentRefiner.filter(
        residentAdminDomainStore.residents,
        residentAdminUiStore.currentResidentAdminListSearchTerm,
        residentAdminUiStore.statusFilter
      ).length,
    [
      residentAdminDomainStore.residents,
      residentAdminUiStore.currentResidentAdminListSearchTerm,
      residentAdminUiStore.statusFilter
    ]
  );

  useEffect(() => {
    // The number of residents has changed, reset the page number to 1
    setPageNumber(1);
  }, [displayResidentsLength]);

  const canSort = useMemo(
    () => displayResidentsLength > 0,
    [displayResidentsLength]
  );

  const shouldShowResidentsTable =
    residentAdminUiStore.isListLoading ||
    residentAdminUiStore.isNavigating ||
    displayResidents.length > 0 ||
    !residentAdminDomainStore.hasLoadedResidentData;

  const sortHandler = (
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ): void => {
    // PaginatedDataTable fires this event on paging and omits the sortField and sortOrder
    // Check for undefined values and set them to the current values
    if (sortField) {
      setSortField(sortField);
      residentAdminUiStore.setCurrentSortField(sortField);
    }
    if (sortOrder) {
      setSortOrder(sortOrder);
      residentAdminUiStore.setCurrentSortOrder(sortOrder);
    }
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "platformUserId",
      hidden: true,
      text: "Id"
    },
    {
      dataField: "lastName",
      text: t("residentadmin:labels.lastName"),
      classes: "last-name",
      sort: canSort,
      headerFormatter: headerLabelFormatter,
      formatter: lastNameFormatter
    },
    {
      dataField: "firstName",
      text: t("residentadmin:labels.firstName"),
      sort: canSort,
      classes: "first-name",
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: "preferredName",
      text: t("residentadmin:labels.preferredName"),
      sort: canSort,
      classes: "preferred-name",
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: "personTypeId",
      text: t("residentadmin:labels.area"),
      classes: "area",
      headerClasses: "area",
      sort: false,
      formatter: areaFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: "unitCode",
      text: t(`${namespace}:labels.unitCode`),
      classes: "unit-code",
      sort: canSort,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: "appAccountEmail",
      text: t("residentadmin:labels.appAccountEmail"),
      sort: canSort,
      classes: "email-address",
      formatter: emailFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: "appAccessStatus",
      text: t("residentadmin:labels.accountStatus"),
      sort: canSort,
      classes: "account-status",
      formatter: statusFormatter,
      headerFormatter: headerLabelFormatter
    }
  ];

  return (
    <div
      className="residentadmin-data-list"
      data-testid="residentAdminDataList"
    >
      {shouldShowResidentsTable ? (
        <PaginatedDataTable
          data={displayResidents}
          keyField="platformUserId"
          columns={columns}
          sizePerPage={sizePerPage}
          onPrevPage={onPageNumber}
          onNextPage={onPageNumber}
          onPageNumberSelected={onPageNumber}
          dataLength={displayResidentsLength}
          onPageChange={onPageChange}
          onSort={sortHandler}
          remote={true}
          currentPageNumber={pageNumber}
          noDataIndication={<div className="no-data-indicator"></div>}
        />
      ) : (
        noResidentAdminIndication
      )}
    </div>
  );
};

export default observer(ResidentAdminDataList);
